import DOMPurify from 'dompurify';
import { supabase } from './supabase';
import type { TicketScreenshotPayload } from './ticketScreenshots';

export const TICKET_TYPES = ['issue', 'feature_request', 'support_question'] as const;
export type TicketType = (typeof TICKET_TYPES)[number];

export const TICKET_STATUSES = ['open', 'in_progress', 'blocked', 'completed', 'cancelled'] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

export const TICKET_PRIORITIES = ['low', 'medium', 'high', 'urgent'] as const;
export type TicketPriority = (typeof TICKET_PRIORITIES)[number];

export const TICKET_TYPE_META: Record<TicketType, { label: string; helper: string }> = {
  issue: {
    label: 'Report a problem',
    helper: 'Something isn’t working as expected.',
  },
  feature_request: {
    label: 'Request a feature',
    helper: 'A capability is missing or could be improved.',
  },
  support_question: {
    label: 'Ask a question',
    helper: 'I need help using Stream2.',
  },
};

export const TICKET_STATUS_META: Record<TicketStatus, { label: string; className: string }> = {
  open: { label: 'Open', className: 'bg-slate-100 text-slate-700' },
  in_progress: { label: 'In progress', className: 'bg-action-surface text-action' },
  blocked: { label: 'Blocked', className: 'bg-amber-50 text-amber-900' },
  completed: { label: 'Resolved', className: 'bg-emerald-50 text-emerald-800' },
  cancelled: { label: 'Cancelled', className: 'bg-slate-50 text-slate-500' },
};

export const TICKET_PRIORITY_META: Record<TicketPriority, { label: string }> = {
  low: { label: 'Low' },
  medium: { label: 'Medium' },
  high: { label: 'High' },
  urgent: { label: 'Urgent' },
};

export interface TicketStatusUpdate {
  type: string;
  metadata?: Record<string, unknown> | null;
  at: string;
}

export interface TicketScreenshot {
  name: string;
  url: string;
}

export interface TicketReply {
  id: string;
  author: 'customer' | 'support';
  author_name: string;
  body: string;
  screenshots?: TicketScreenshot[];
  at: string;
}

export interface CockpitTicket {
  external_ticket_id: string;
  title: string;
  description: string | null;
  status: TicketStatus;
  priority: TicketPriority;
  ticket_type?: TicketType | null;
  created_at: string;
  updated_at: string;
  status_updates?: TicketStatusUpdate[];
  screenshots?: TicketScreenshot[];
  source_metadata?: { screenshots?: TicketScreenshot[] } | null;
  replies?: TicketReply[];
}

export function sanitizeTicketHtml(html: string): string {
  return String(
    DOMPurify.sanitize(html, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's',
        'ul', 'ol', 'li', 'a', 'img', 'h2', 'h3', 'blockquote',
      ],
      ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'target', 'rel'],
      ALLOW_DATA_ATTR: false,
    }),
  );
}

export function ticketLooksLikeHtml(value: string): boolean {
  return /<\/?[a-z][\s\S]*>/i.test(value);
}

/** TipTap's empty document is `<p></p>`. */
export function isEmptyTicketHtml(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return true;
  if (/<img\b/i.test(trimmed)) return false;
  const text = trimmed
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
  return text.length === 0;
}

type CreateResponse = { created: boolean; ticket: CockpitTicket };
type ListResponse = { tickets: CockpitTicket[] };
type GetResponse = { ticket: CockpitTicket };
type ReplyResponse = { reply: TicketReply; ticket: CockpitTicket };

function errorMessage(data: unknown, fallback: string): string {
  if (data && typeof data === 'object' && 'error' in data) {
    const err = (data as { error?: unknown }).error;
    if (typeof err === 'string' && err.trim()) return err;
    if (err && typeof err === 'object' && 'message' in err) {
      const message = (err as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message;
    }
  }
  return fallback;
}

/** supabase-js leaves the function body on FunctionsHttpError.context and sets data to null. */
async function readFunctionErrorBody(error: unknown): Promise<unknown> {
  if (!error || typeof error !== 'object' || !('context' in error)) return null;
  const context = (error as { context?: unknown }).context;
  if (!(context instanceof Response)) return null;
  const text = await context.text().catch(() => '');
  if (!text.trim()) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return { error: text.trim().slice(0, 300) };
  }
}

const MAX_INVOKE_CHARS = 450_000;

async function invoke<T>(body: Record<string, unknown>): Promise<T> {
  if (JSON.stringify(body).length > MAX_INVOKE_CHARS) {
    throw new Error('This ticket is too large to send. Use a smaller image or shorten the text.');
  }
  const { data, error } = await supabase.functions.invoke<T & { error?: unknown }>('cockpit_tickets', {
    body,
  });

  if (error) {
    const bodyError = await readFunctionErrorBody(error);
    const fallback = /edge function/i.test(error.message ?? '')
      ? 'Could not reach support. Try again in a moment.'
      : error.message || 'Request failed';
    throw new Error(errorMessage(bodyError ?? data, fallback));
  }
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(errorMessage(data, 'Request failed'));
  }
  return data as T;
}

type StoredScreenshot = { name: string; url: string };

const INLINE_DATA_URL = /src="(data:image\/(?:png|jpeg|jpg|gif|webp);base64,[^"]+)"/gi;

function contentTypeFromDataUrl(dataUrl: string): TicketScreenshotPayload['content_type'] {
  const match = /^data:(image\/(?:png|jpeg|gif|webp));base64,/i.exec(dataUrl);
  if (match?.[1] === 'image/png' || match?.[1] === 'image/gif' || match?.[1] === 'image/webp') {
    return match[1];
  }
  return 'image/jpeg';
}

/** One image per request. A single create body that embeds screenshots is dropped by the gateway. */
export function uploadTicketImage(payload: TicketScreenshotPayload): Promise<StoredScreenshot> {
  return invoke<{ screenshot: StoredScreenshot }>({
    action: 'upload_image',
    screenshot: payload,
  }).then((result) => {
    if (!result.screenshot?.url) throw new Error('Could not store the image');
    return result.screenshot;
  });
}

async function stageInlineImages(html: string): Promise<string> {
  const matches = [...html.matchAll(INLINE_DATA_URL)];
  let next = html;
  for (const match of matches) {
    const dataUrl = match[1];
    const stored = await uploadTicketImage({
      name: 'image.jpg',
      content_type: contentTypeFromDataUrl(dataUrl),
      data_base64: dataUrl.replace(/^data:[^;]+;base64,/, ''),
    });
    next = next.replace(dataUrl, stored.url);
  }
  return next;
}

export async function createTicket(input: {
  title: string;
  ticket_type: TicketType;
  description?: string;
  priority?: TicketPriority;
  page?: string;
  screenshots?: TicketScreenshotPayload[];
}): Promise<CreateResponse> {
  const description = input.description ? await stageInlineImages(input.description) : undefined;
  const screenshots: StoredScreenshot[] = [];
  for (const shot of input.screenshots ?? []) {
    screenshots.push(await uploadTicketImage(shot));
  }
  return invoke<CreateResponse>({
    action: 'create',
    title: input.title,
    ticket_type: input.ticket_type,
    description,
    priority: input.priority,
    page: input.page,
    screenshots,
  });
}

export function listTickets(): Promise<ListResponse> {
  return invoke<ListResponse>({ action: 'list' });
}

export function getTicket(externalTicketId: string): Promise<GetResponse> {
  return invoke<GetResponse>({ action: 'get', external_ticket_id: externalTicketId });
}

export function replyToTicket(input: {
  external_ticket_id: string;
  body: string;
  screenshots?: TicketScreenshotPayload[];
}): Promise<ReplyResponse> {
  return invoke<ReplyResponse>({ action: 'reply', ...input });
}
